import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { IAppState } from '../reducers';
import { updateAsset as apiUpdateAsset, getAsset as apiGetAsset } from '../../api/vault/assets';
import { generateAssetMediaInformation } from '../../utils/assetDetails/generateAssetMediaInformation';
import { generateRenderableAssetDetails, generateEditableAssetDetails } from '../../utils/assetDetails/generateRenderableAssetDetails';
import { DEFAULT_MESSAGE } from '../previewAsset';
import { selectActiveAssetDetails, updateAssetSource } from './searchResults';
import { queryGamesAction } from '../search';
import { selectUserRole } from '../user/userOrgRole';
import toast from '../../utils/toast';
import { prepareAssetUpdates } from '../../utils/assetDetails/prepareAssetUpdates';

export interface IMediaInformation {
  type: string;
  source: string;
  fullSizeSource?: string;
  sourceSource: string;
  message: string;
}

export interface IState {
  gameKey: string;
  assetId: string;
  isLoading: boolean;
  mediaInformation: IMediaInformation;
  rawAssetDetails: any;
  editableAssetDetails: Array<any>;
  rawAssetDetailsCopy: any;
  editableAssetDetailsCopy: Array<any>;
  isEditable: boolean;
  assetUpdates: any;
}

export enum ActionType {
  Activate = 'ExchangeActiveAsset.Activate',
  StartLoading = 'ExchangeActiveAsset.StartLoading',
  FinishLoading = 'ExchangeActiveAsset.FinishLoading',
  UpdateMediaInformation = 'ExchangeSearchResults.UpdateMediaInformation',
  UpdateAssetDetails = 'ExchangeSearchResults.UpdateAssetDetails',
  CloneAssetDetails = 'ExchangeSearchResults.CloneAssetDetails',
  ToggleEditable = 'ExchangeActiveAsset.ToggleEditable',
  Restore = 'ExchangeActiveAsset.Restore',
  Reset = 'ExchangeActiveAsset.Reset',
  UpdateAssetUpdates = 'ExchangeActiveAsset.UpdateAssetUpdates',
  ResetAssetUpdates = 'ExchangeActiveAsset.ResetAssetUpdates',
}

export interface IExchangeSearchResultsAction extends Action<ActionType> {
  payload?: any,
}

const INITIAL_STATE: IState = {
  gameKey: null,
  assetId: null,
  isLoading: false,
  mediaInformation: {
    type: null,
    source: null,
    sourceSource: null,
    message: DEFAULT_MESSAGE,
  },
  rawAssetDetails: null,
  editableAssetDetails: null,
  rawAssetDetailsCopy: null,
  editableAssetDetailsCopy: null,
  isEditable: false,
  assetUpdates: {},
};

export default function reducer(state: IState = INITIAL_STATE, action: IExchangeSearchResultsAction): IState {
  switch (action.type) {
    case ActionType.Activate:
      return {
        ...state,
        gameKey: action.payload.gameKey,
        assetId: action.payload.assetId,
        isEditable: false,
      };
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
      };
    case ActionType.UpdateMediaInformation:
      return {
        ...state,
        mediaInformation: action.payload.mediaInformation,
      };
    case ActionType.UpdateAssetDetails:
      return {
        ...state,
        rawAssetDetails: action.payload.rawAssetDetails,
        editableAssetDetails: action.payload.editableAssetDetails,
      };
    case ActionType.CloneAssetDetails:
      return {
        ...state,
        rawAssetDetailsCopy: {
          ...state.rawAssetDetails,
        },
        editableAssetDetailsCopy: _.cloneDeep(state.editableAssetDetails),
      };
    case ActionType.ToggleEditable:
      return {
        ...state,
        isEditable: !state.isEditable,
      };
    case ActionType.Restore:
      return {
        ...state,
        rawAssetDetails: {
          ...state.rawAssetDetailsCopy,
        },
        editableAssetDetails: _.cloneDeep(state.editableAssetDetailsCopy),
      };
    case ActionType.UpdateAssetUpdates:
      return {
        ...state,
        assetUpdates: {
          ...state.assetUpdates,
          ...action.payload,
        },
      };
    case ActionType.ResetAssetUpdates:
      return {
        ...state,
        assetUpdates: {},
      };
    case ActionType.Reset:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const restoreActiveAsset = () => ({
  type: ActionType.Restore,
});

export const resetActiveAsset = () => ({
  type: ActionType.Reset,
});

export const toggleAssetEditable = () => ({
  type: ActionType.ToggleEditable,
});

export const updateAssetDetails = (
  rawAssetDetails: object,
  editableAssetDetails: object,
) => ({
  type: ActionType.UpdateAssetDetails,
  payload: {
    rawAssetDetails,
    editableAssetDetails,
  },
});

// @ts-ignore
const metaTagsSelector = (state: IAppState) => state.metatags?.metaTags ?? [];
const rawAssetDetailsSelector = (state: IAppState) => state.exchange?.activeAsset?.rawAssetDetails;
const securityProfileIdValueMappingSelector = (state: IAppState) => state.securityProfiles?.idValueMapping ?? [];

export const selectRenderableAssetDetails = createSelector(
  metaTagsSelector,
  rawAssetDetailsSelector,
  securityProfileIdValueMappingSelector,
  (
    metaTags,
    rawAssetDetails,
    securityProfileIdValueMapping,
  ) => {
    if (rawAssetDetails === null) {
      return [];
    }
    return generateRenderableAssetDetails(
      metaTags,
      rawAssetDetails,
      securityProfileIdValueMapping,
    );
  },
);

export const selectEditableAssetDetails = createSelector(
  metaTagsSelector,
  rawAssetDetailsSelector,
  securityProfileIdValueMappingSelector,
  (
    metaTags,
    rawAssetDetails,
    securityProfileIdValueMapping,
  ) => {
    if (rawAssetDetails === null) {
      return [];
    }
    return generateRenderableAssetDetails(
      metaTags,
      rawAssetDetails,
      securityProfileIdValueMapping,
    );
  },
);

export const activateAsset = (gameKey: string, assetId: string) => async (
  dispatch: Dispatch<IExchangeSearchResultsAction>,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.Activate,
    payload: {
      gameKey,
      assetId,
    },
  });

  dispatch({ type: ActionType.StartLoading });

  const { _id } = selectActiveAssetDetails(getState());
  const rawAssetDetails = (await apiGetAsset(_id)).data?.data;

  let mediaInformation;
  try {
    mediaInformation = await generateAssetMediaInformation(rawAssetDetails);
  } catch (e) {
    const { mediaInformation: initMediaInformation } = INITIAL_STATE;
    mediaInformation = initMediaInformation;
  }

  dispatch({
    type: ActionType.UpdateMediaInformation,
    payload: {
      mediaInformation,
    },
  });

  const state = getState();
  const metaTags = metaTagsSelector(state);
  const securityProfileIdValueMapping = securityProfileIdValueMappingSelector(state);
  const editableAssetDetails = generateEditableAssetDetails(
    metaTags,
    rawAssetDetails,
    securityProfileIdValueMapping,
  );

  dispatch({
    type: ActionType.UpdateAssetDetails,
    payload: {
      rawAssetDetails,
      editableAssetDetails,
    },
  });
  dispatch(resetAssetUpdates());
  dispatch({ type: ActionType.CloneAssetDetails });
  dispatch({ type: ActionType.FinishLoading });
};

export const resetAssetUpdates = () => ({
  type: ActionType.ResetAssetUpdates,
});

export const addAssetUpdates = (update: {}) => ({
  type: ActionType.UpdateAssetUpdates,
  payload: update,
});

export const updateAsset = () => async (
  dispatch: Dispatch<IExchangeSearchResultsAction>,
  getState: () => IAppState,
) => {
  const state = getState();
  const metaTags = metaTagsSelector(state);
  const userRole = selectUserRole(state);
  const securityProfileIdValueMapping = securityProfileIdValueMappingSelector(state);
  const { exchange: { activeAsset: { assetUpdates, rawAssetDetails, gameKey } } } = state;
  dispatch({ type: ActionType.StartLoading });

  if (!assetUpdates || _.isEmpty(assetUpdates)) {
    dispatch(toggleAssetEditable());
    dispatch({ type: ActionType.FinishLoading });
    return;
  }

  const { updates, error } = prepareAssetUpdates(assetUpdates, {
    displayName: userRole.userInfo.displayName,
    gameDateMode: 'dropAlways',
  });
  if (error) {
    // Leave the panel in edit mode so the field can be corrected.
    toast(error, { type: toast.TYPE.ERROR });
    dispatch({ type: ActionType.FinishLoading });
    return;
  }

  const res = await apiUpdateAsset(rawAssetDetails._id, updates);
  dispatch(resetAssetUpdates());
  if (res.status === 200 && res.data?.data) {
    const details = res.data?.data;

    // va-1229 + va-1083
    // in the event the video is an exchange asset and the title is changed =>
    //   - reload search results
    //   - reset preview panel
    //
    // The re-query alone is not enough to show what was just saved: results come from
    // OpenSearch, which is not searchable until its refresh interval elapses, so a search
    // this soon after the write can return the pre-save document and replace the list with
    // stale values. Patching the hit from the response as a side effect - after the fresh
    // results have landed, or the patch would be overwritten by them - closes that window.
    queryGamesAction(500, () => {
      // Cast because this dispatches into the searchResults slice, which types its actions
      // separately from this one - the same reason the thunks here take Dispatch<any>.
      (dispatch as Dispatch<any>)(updateAssetSource(gameKey, details._id, details));
    })(dispatch, getState);

    const editableAssetDetails = generateEditableAssetDetails(
      metaTags,
      details,
      securityProfileIdValueMapping,
    );

    dispatch({
      type: ActionType.UpdateAssetDetails,
      payload: {
        rawAssetDetails: details,
        editableAssetDetails,
      },
    });
  }
  dispatch(toggleAssetEditable());
  dispatch({ type: ActionType.FinishLoading });
};
